// ============================================================
// categorias.js — as categorias cadastradas de cada modalidade
//
// A lista nascia do que cada um digitava no cadastro do atleta. Funcionava,
// e cobrava caro: "Girls 12 anos" e "Girls - 12 anos" entravam como duas
// categorias, o quadro de medalhas saía partido ao meio e o erro só
// aparecia no relatório pronto.
//
// Agora a coordenação escreve a lista uma vez, na modalidade, dentro do
// núcleo. O cadastro do atleta escolhe dessa lista, e o lançamento de
// resultado também.
//
// A categoria do cadastro é a PRINCIPAL, a da idade do atleta. Em algumas
// modalidades ele disputa mais de uma no mesmo evento, e essa outra é
// escolhida no lançamento do resultado, como secundária. Por isso a lista
// cadastrada serve aos dois lugares, e não só ao cadastro.
//
// Mesma regra do servidor (functions/resultados.js). Repetida aqui porque
// painel e functions não compartilham código, e a chave precisa dar igual
// dos dois lados.
// ============================================================

const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);

export function chaveCategoria(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(DIACRITICOS, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// As categorias de uma modalidade, do mapa guardado no núcleo.
//
// Devolve lista vazia quando a modalidade ainda não tem nenhuma, e é isso
// que diz à tela que ali a categoria continua sendo digitada à mão: obrigar
// a escolher de uma lista vazia pararia o cadastro em todo núcleo que ainda
// não preencheu.
export function categoriasDaModalidade(mapa, modalidade) {
  const m = mapa && typeof mapa === "object" ? mapa : {};
  const k = chaveCategoria(modalidade);
  if (!k) return [];
  const achado = m[k] !== undefined ? m[k] : m[modalidade];
  return Array.isArray(achado) ? achado.filter(Boolean).map((x) => String(x)) : [];
}

// A lista para um seletor, com o que o atleta já tem gravado incluído.
//
// Sem isso, abrir o cadastro de quem foi registrado antes da lista, ou com
// uma categoria que a coordenação depois tirou, mostraria o campo vazio: a
// tela diria que ele não tem categoria, e salvar apagaria a que tinha.
export function opcoesDeCategoria(mapa, modalidade, atual) {
  const lista = categoriasDaModalidade(mapa, modalidade);
  const texto = String(atual || "").trim();
  if (!texto) return lista;
  if (lista.some((c) => chaveCategoria(c) === chaveCategoria(texto))) return lista;
  return [...lista, texto];
}

// ── A ordem em que as categorias são oferecidas a UM atleta ──
//
// No BMX e no skate são poucas categorias e a lista inteira cabe num
// seletor. No tênis de mesa são quase sessenta: Sub-9 a Sub-21 e Absoluto
// A a F, cada uma delas também em dupla, em equipe de clube e em seleção.
// Uma lista desse tamanho, em ordem alfabética, tira a praticidade de quem
// lança resultado com a folha do evento na mão.
//
// Duas coisas resolvem sem esconder nada. A primeira é tirar o que não
// serve: dupla masculina não é oferecida a atleta do sexo feminino, e
// vice-versa. A segunda é pôr na frente o que o atleta realmente disputa,
// que é a faixa da idade dele.
//
// Nada é removido além do gênero oposto: o atleta sobe de categoria, joga
// absoluto, entra em equipe. Tudo continua na lista, só que depois.

const SO_MASCULINO = /\bmasc(\b|ulin)/i;
const SO_FEMININO = /\bfem(\b|inin)/i;

// A faixa etária que a categoria nomeia, quando ela nomeia alguma.
export function faixaDaCategoria(texto) {
  const achado = String(texto || "").match(/\bsub\s*-?\s*(\d{1,2})\b/i);
  return achado ? Number(achado[1]) : null;
}

// A faixa em que o atleta joga: a menor Sub-N que ainda o comporta.
//
// Sub-15 é de quem ainda não fez 15, então quem tem 13 joga nela. Sem
// idade no cadastro não há faixa, e a lista sai na ordem cadastrada.
export function faixaDoAtleta(idade, faixas) {
  const anos = Number(idade);
  if (!Number.isFinite(anos) || anos < 1) return null;
  const candidatas = (faixas || []).filter((n) => Number.isFinite(n) && n > anos);
  return candidatas.length ? Math.min(...candidatas) : null;
}

export function ordenarParaAtleta(opcoes, { idade = null, sexo = "" } = {}) {
  const lista = Array.isArray(opcoes) ? opcoes.filter(Boolean) : [];
  const genero = String(sexo || "").toLowerCase();

  // Categoria do gênero oposto sai da lista. "Mista" fica para os dois.
  const servem = lista.filter((c) => {
    if (genero === "masculino" && SO_FEMININO.test(c) && !/mist/i.test(c)) return false;
    if (genero === "feminino" && SO_MASCULINO.test(c) && !/mist/i.test(c)) return false;
    return true;
  });

  const faixa = faixaDoAtleta(idade, servem.map(faixaDaCategoria));
  if (faixa === null) return servem;

  // Três grupos, nesta ordem: a faixa dele, as outras faixas, e o que não
  // tem faixa nenhuma (Absoluto). Dentro de cada grupo, a ordem cadastrada
  // é mantida, porque é a ordem em que as provas correm.
  const daFaixa = servem.filter((c) => faixaDaCategoria(c) === faixa);
  const deOutraFaixa = servem.filter((c) => {
    const f = faixaDaCategoria(c);
    return f !== null && f !== faixa;
  });
  const semFaixa = servem.filter((c) => faixaDaCategoria(c) === null);
  return [...daFaixa, ...deOutraFaixa, ...semFaixa];
}

// ── A regra: categoria pertence a UMA modalidade ─────────────
//
// Toda lista de escolha de categoria sai daqui, e é isto que garante que
// "Sub-19 - Dupla Mista" do tênis de mesa nunca apareça no BMX.
//
// A lista de uma modalidade é composta de duas origens, nesta ordem:
//
//   1. as CADASTRADAS dela, em Núcleos, na ordem em que a coordenação as
//      escreveu, que é a ordem em que as provas correm;
//   2. as EM USO por atletas ou lançamentos DAQUELA MESMA modalidade.
//
// A segunda origem existe porque tirar uma categoria do cadastro não pode
// fazer sumir a que está gravada em quem a disputou: a competição já
// aconteceu, e o resultado dela continua valendo. Mas ela só traz o que é
// da modalidade: antes a lista juntava a categoria de QUALQUER atleta do
// núcleo, e bastava um atleta de tênis de mesa para o campo do BMX passar
// a sugerir categoria de tênis de mesa.
//
// Vale para núcleo e modalidade que ainda nem existem: nada aqui conhece
// nome de modalidade, a comparação é sempre entre a modalidade do item e a
// modalidade pedida.
export function categoriasDisponiveis({ mapa, modalidade, emUso = [] } = {}) {
  const alvo = chaveCategoria(modalidade);
  const vistas = new Map();

  const juntar = (texto) => {
    const limpo = String(texto || "").trim();
    if (!limpo) return;
    const k = chaveCategoria(limpo);
    if (k && !vistas.has(k)) vistas.set(k, limpo);
  };

  categoriasDaModalidade(mapa, modalidade).forEach(juntar);

  // Sem modalidade pedida não há a que pertencer, e aí nada de "em uso"
  // entra: oferecer tudo seria o mesmo defeito com outro nome.
  if (alvo) {
    (Array.isArray(emUso) ? emUso : []).forEach((item) => {
      if (!item) return;
      if (chaveCategoria(item.modalidade) !== alvo) return;
      juntar(item.categoria);
    });
  }

  return [...vistas.values()];
}
